import axios from "axios";
import {
  AvailabilityRequest,
  AvailabilityResponse,
  VIEWED_PC_AVAILABILITY_LOADING,
} from "redmond";
import dayjs from "dayjs";
import { config } from "../../config";

import { hotelAvailabilityApiPrefix } from "../paths";
import { trackEvent } from "../analytics/trackEvent";

// Defensive in-flight dedupe — coalesces *byte-identical* concurrent requests
// only. Scope is narrow: same call site re-firing (React strict mode in dev,
// hot reload, a saga re-entrancy bug). Does NOT collapse the cross-saga race
// between the availability page (LodgingSelection.Location, no progressiveConfig)
// and the shop page setup (LodgingSelection.LodgingIds, progressiveConfig:{pageSize:10})
// — those bodies are structurally different. The cache-reuse logic in
// fetchPremierCollectionShopSaga.ts is what addresses that case.
//
// Key assumption: callers build the request body with stable key ordering.
// V8 preserves insertion order for string keys, so a single call site is
// always self-consistent; cross-site collisions would only occur for
// equivalent bodies built identically, which is the only case worth deduping.
const inFlight = new Map<string, Promise<AvailabilityResponse>>();
// 100ms is long enough to absorb synchronous downstream redispatches
// (e.g. component re-render triggering the same fetch on the next tick),
// short enough that it can never act as a stale cache.
const IN_FLIGHT_RETENTION_MS = 100;

export const fetchPremierCollectionAvailability = (
  body: AvailabilityRequest
): Promise<AvailabilityResponse> => {
  const key = JSON.stringify(body);
  const existing = inFlight.get(key);
  if (existing != null) return existing;

  const startTime = dayjs();
  const promise = axios
    .post(hotelAvailabilityApiPrefix, body, { baseURL: config.baseURL })
    .then((res) => {
      trackEvent({
        eventName: VIEWED_PC_AVAILABILITY_LOADING,
        properties: {
          runtime: dayjs().diff(startTime, "seconds", true),
        },
      });
      return res.data as AvailabilityResponse;
    })
    .finally(() => {
      setTimeout(() => {
        if (inFlight.get(key) === promise) inFlight.delete(key);
      }, IN_FLIGHT_RETENTION_MS);
    });

  inFlight.set(key, promise);
  return promise;
};
